/**
 * Pure utility functions for the CascadeSettingsModal module.
 * No React or Redux dependencies.
 */
import { iCompanyGroup } from "../../../cached-data/types"
import { CICO_PARENT_CHILDREN } from "./constants"
import { CascadeSettingKey, EntityType, Excluded, M2MNameMaps, PreflightEntity } from "./types"

/**
 * Get a flat DFS-ordered list of all descendant groups with their visual depth.
 *
 * Parameters:
 * rootIds (number[]): Direct children IDs to start from.
 * allGroups (Record<number, iCompanyGroup>): All groups keyed by ID from Redux.
 * depth (number): Current recursion depth (0 = direct children of source group).
 *
 * Returns:
 * Array<{ group: iCompanyGroup; depth: number }>: Ordered list of descendants.
 */
export function getDescendants(rootIds: number[], allGroups: Record<number, iCompanyGroup>, depth = 0): Array<{ group: iCompanyGroup; depth: number }> {
    const result: Array<{ group: iCompanyGroup; depth: number }> = []
    for (const id of rootIds) {
        const g = allGroups[id]
        if (!g) continue
        result.push({ group: g, depth })
        if (g.children_ids?.length) {
            result.push(...getDescendants(g.children_ids, allGroups, depth + 1))
        }
    }
    return result
}

/**
 * Returns a new Set with the item added or removed based on `include`.
 *
 * Parameters:
 * set (Set<T>): The source set.
 * item (T): The item to add or remove.
 * include (boolean): True to add, false to remove.
 *
 * Returns:
 * Set<T>: A new set with the change applied.
 */
export function toggleSetMember<T>(set: Set<T>, item: T, include: boolean): Set<T> {
    const next = new Set(set)
    if (include) next.add(item)
    else next.delete(item)
    return next
}

/**
 * Build a state-updater function that adds or removes a list of entities from
 * the exclusion set for a given setting key and entity type.
 * Cascades to CICO child settings via CICO_PARENT_CHILDREN when applicable.
 *
 * Parameters:
 * settingKey (CascadeSettingKey): The setting being modified.
 * entityType ("projects" | "subgroups"): Entity type.
 * entities (PreflightEntity[]): Entities to add to or remove from the exclusion set.
 * exclude (boolean): True to exclude (add), false to restore (remove).
 *
 * Returns:
 * (prev: Excluded) => Excluded: An immutable state updater suitable for setExcluded.
 */
export function buildAllExclusionUpdate(
    settingKey: CascadeSettingKey,
    entityType: EntityType,
    entities: PreflightEntity[],
    exclude: boolean,
): (prev: Excluded) => Excluded {
    const keysToUpdate = [settingKey, ...(CICO_PARENT_CHILDREN[settingKey] ?? [])]
    return prev => {
        const next = { ...prev }
        for (const key of keysToUpdate) {
            const idField: keyof Excluded[string] = entityType === "projects" ? "projectIds" : "subgroupIds"
            const existing = next[key] ?? { projectIds: new Set<number>(), subgroupIds: new Set<number>() }
            const updatedIds = new Set(existing[idField])
            for (const e of entities) {
                if (exclude) {
                    updatedIds.add(e.id)
                } else {
                    updatedIds.delete(e.id)
                }
            }
            next[key] = { ...existing, [idField]: updatedIds }
        }
        return next
    }
}

/**
 * Format a MetadataCapture enum string for display.
 *
 * Parameters:
 * value (string | undefined): The raw enum value (e.g. "REQUIRED").
 *
 * Returns:
 * string: Sentence-cased label (e.g. "Required"), or "Not set" if absent.
 */
export function formatMetadataCapture(value: string | undefined): string {
    if (!value) return "Not set"
    return value.charAt(0) + value.slice(1).toLowerCase()
}

/**
 * Format a sorted comma-separated list of names from an ID array using a name lookup map.
 * Falls back to "None" if the list is empty, or to a count string if names are not yet loaded.
 *
 * Parameters:
 * ids (number[]): Member IDs.
 * nameMap (Record<number, string>): ID to display name mapping.
 * singularNoun (string): Singular noun for the count fallback (e.g. "cohort").
 *
 * Returns:
 * string: Comma-separated names, count string if names unavailable, or "None".
 */
export function formatNameList(ids: number[], nameMap: Record<number, string>, singularNoun: string): string {
    if (!ids.length) return "None"
    const names = ids.map(id => nameMap[id]).filter(Boolean).sort()
    if (names.length) return names.join(", ")
    // Names not loaded yet — fall back to count
    return `${ids.length} ${ids.length === 1 ? singularNoun : `${singularNoun}s`}`
}

/**
 * Return a short hint string showing the group's current value for a setting,
 * to be displayed in the Step 2 preview detail header.
 *
 * Parameters:
 * settingKey (CascadeSettingKey): The setting being displayed.
 * group (iCompanyGroup): The source group.
 * nameMaps (M2MNameMaps): Name lookups for M2M entity IDs, built from Redux state.
 *
 * Returns:
 * string | null: Hint text, or null if no useful hint is available.
 */
export function getSettingValueHint(settingKey: CascadeSettingKey, group: iCompanyGroup, nameMaps: M2MNameMaps): string | null {
    const cico = group.cico_settings
    switch (settingKey) {
        case "logo":
            return null
        case "attestation_language": {
            if (!group.attestation_language) return "Not set"
            const text = group.attestation_language
            return text.length > 40 ? text.slice(0, 40) + "…" : text
        }
        case "cohorts":
            return formatNameList(group.cohorts ?? [], nameMaps.cohorts, "cohort")
        case "rate_tables":
            return formatNameList(group.rate_tables ?? [], nameMaps.rateTables, "rate table")
        case "work_rule_sets":
            return formatNameList((group.work_rule_sets ?? []).map(ws => ws.id), nameMaps.workRuleSets, "work rule set")
        case "pto_buckets":
            return null
        case "grants_employee_access":
            return group.grants_employee_access ? "Yes" : "No"
        case "grants_project_access":
            return group.grants_project_access ? "Yes" : "No"
        case "cico_enabled":
            return cico.enabled ? "Enabled" : "Disabled"
        case "cico_rounding":
            return cico.rounding_increment != null ? `${cico.rounding_increment} min` : "None"
        case "cico_geofence": {
            const fenceHints = []
            if (cico.geofence) fenceHints.push("Custom fence")
            if (cico.custom_address) fenceHints.push(cico.custom_address)
            if (cico.radius) fenceHints.push(`${cico.radius} ${cico.units ?? ""}`.trim())
            if (fenceHints.length) return fenceHints.join(" • ")
            return "Not set"
        }
        case "cico_breaks":
            return cico.breaks_enabled ? "Enabled" : "Disabled"
        case "cico_meals":
            return cico.meals_enabled ? "Enabled" : "Disabled"
        case "cico_location":
            return formatMetadataCapture(cico.location)
        case "cico_photo":
            return formatMetadataCapture(cico.photo)
        case "cico_location_employee_overrides":
            return formatNameList(cico.location_employee_overrides ?? [], nameMaps.employees, "employee")
        case "cico_location_cohort_overrides":
            return formatNameList(cico.location_cohort_overrides ?? [], nameMaps.cohorts, "cohort")
        case "cico_photo_employee_overrides":
            return formatNameList(cico.photo_employee_overrides ?? [], nameMaps.employees, "employee")
        case "cico_photo_cohort_overrides":
            return formatNameList(cico.photo_cohort_overrides ?? [], nameMaps.cohorts, "cohort")
    }
}
